import { AutoComplete, Form, Input, InputNumber, Select } from "antd";
import { Button as MuiButton } from "@mui/material";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import {
  getAutocompleteSuggestions,
  getAllBanks,
} from "israeli-bank-autocomplete";
import isIsraeliIdValid from "israeli-id-validator";
import { validateBankAccount, RESULT } from "israeli-bank-validation";
import Modal from "antd/lib/modal/Modal";
import { useWindowHeight, useWindowWidth } from "@react-hook/window-size";
import { useTranslation } from "react-i18next";
import { MOBILE_BREAK } from "../constants/constants";
import { publicDataApiService } from "../functions/apiService";
import { filterAndSetField } from "../functions/helpers";

const bankOptions = getAllBanks().map((bank) => ({
  label: `${bank.bankCode} - ${bank.bankName}`,
  value: String(bank.bankCode),
}));

const layout = {
  labelCol: { span: 100 },
  wrapperCol: { span: 100 },
};

const areFieldsValid = ({
  accountId,
  amount,
  bankId,
  branchId,
  payeeId,
  payeeName,
  payeeNumber,
}) => {
  if (isNaN(parseInt(bankId)) || parseInt(bankId) < 0 || parseInt(bankId) > 99)
    return false;
  if (
    isNaN(parseInt(branchId)) ||
    parseInt(branchId) < 0 ||
    parseInt(branchId) > 999
  )
    return false;
  if (isNaN(parseInt(accountId)) || accountId.length > 9) return false;
  if (isNaN(parseInt(payeeId)) || payeeId.length > 9) return false;
  if (isNaN(parseInt(amount)) || parseInt(amount) < 0) return false;
  if (!payeeName || payeeName.length > 16) return false;
  if (payeeNumber && payeeNumber.length > 20) return false;
  return true;
};

export const PayeeForm = forwardRef(({ onFormFinishClick, favorites }, ref) => {
  const { t } = useTranslation("online-convertor");
  const [isEdit, setIsEdit] = useState("");
  const [branchOptions, setBranchOptions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const height = useWindowHeight();
  const width = useWindowWidth();
  const [companyName, setCompanyName] = useState("");

  const [form] = Form.useForm();
  const [fieldsValid, setFieldsValid] = useState(false);
  const [selectedKey, setSelectedKey] = useState("");//to reset the dropdown everytime
  const [searchTerm, setSearchTerm] = useState("");

  const fetchAndSetCompanyNameIfCompany = async (payeeIdValue) => {
    try {
      if (!/^\d{9}$/.test(payeeIdValue)) {
        setCompanyName("");
        return;
      }
      const isValidIsraeliId = isIsraeliIdValid(payeeIdValue);
      const isCompanyId = isValidIsraeliId && /^5\d{8}$/.test(payeeIdValue);
      if (!isCompanyId) {
        setCompanyName("");
        return;
      }
      const result = await publicDataApiService.getCompanyByHp(payeeIdValue);
      const records = result && result.result && Array.isArray(result.result.records)
        ? result.result.records
        : [];
      if (records.length) {
        const name = records[0]['שם חברה'] || "";
        setCompanyName(name || "");
      } else {
        setCompanyName("");
      }
    } catch (_) {
      setCompanyName("");
    }
  };

  const onSearch = (searchText) => {
    const bankId = form.getFieldValue("bankId");
    const options = getAutocompleteSuggestions(searchText, {
      bankCode: bankId ? parseInt(bankId) : null,
      inputType: "BRANCH_CODE",
    }).map((branch) => ({
      value: String(branch.branchCode),
      label: `${branch.branchCode} - ${branch.city}, ${branch.branchAddress}`,
    }));
    setBranchOptions(
      options.length && searchText
        ? options
        : [{ label: searchText, value: searchText }]
    );
  };
  const onSubmit = useCallback(() => {
    setModalOpen(false);
    onFormFinishClick?.(form.getFieldsValue(), isEdit);
  }, [isEdit, form, onFormFinishClick]);

  useImperativeHandle(ref, () => ({
    updateRow: (row) => {
      setIsEdit(row.key);
      setFieldsValid(areFieldsValid(row));
      setCompanyName("");
      form.setFieldsValue(row);
      // Populate company name in edit mode if applicable
      if (row && row.payeeId) {
        fetchAndSetCompanyNameIfCompany(String(row.payeeId));
      }
      setModalOpen(true);
      setSelectedKey("");
    },
    addRow: () => {
      setIsEdit("");
      setFieldsValid(false);
      form.resetFields();
      setCompanyName("");
      setModalOpen(true);
      setSelectedKey("");
    },
  }));
  //A function fills in all fields that are increased after selection from the dropdown 
  const handleSelect = (selectedKey) => {
  setSelectedKey(selectedKey);
  setCompanyName("");

  const found = favorites.find((fav) => String(fav.payee_id) === String(selectedKey));

  if (found) {
    form.setFieldsValue({
      bankId: found.bank_id,
      branchId: found.branch_id,
      accountId: found.account_id,
      payeeName: found.payee_name,
      payeeId: found.payee_id,
      payeeNumber: found.payee_number,
    });
    // Trigger company lookup for selected favorite if applicable
    if (found.payee_id) {
      fetchAndSetCompanyNameIfCompany(String(found.payee_id));
    }
  } else {
    form.resetFields();
  }
};


  return (
    <Modal
      className="full-screen"
      onCancel={() => {
        form.resetFields();
        setCompanyName("");
        setModalOpen(false);
      }}
      style={{ top: 20 }}
      bodyStyle={{
        maxHeight: height - (width < MOBILE_BREAK ? 20 : 100),
        overflowY: "auto",
      }}
      title={t(`employee-form-${isEdit ? "edit" : "add"}-title`)}
      visible={modalOpen}
      footer={null}
    >
      <div>
        <Select
          value={selectedKey || undefined}
          onChange={handleSelect}
          placeholder={t("recent-records")}
          showSearch
          onSearch={setSearchTerm}
          filterOption={false}
          options={
            Array.isArray(favorites)
              ? favorites
                  .filter((tx) => 
                    tx.payee_name.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((tx) => ({
                    label: tx.payee_name,
                    value: tx.payee_id
                  }))
              : []
          }
          allowClear
          style={{ width: '50%' }}
        />

        <Form
          {...layout}
          onValuesChange={(_, values) => setFieldsValid(areFieldsValid(values))}
          form={form}
          name="control-hooks"
          layout="vertical"
        >
          <Form.Item
            name="bankId"
            tooltip={{
              title: t("bank-id-tooltip"),
              icon: <InfoCircleOutlined />,
            }}
            label={t("bank-id-label")}
            rules={[{ required: true, message: t("messages-field-is-required") }]}
          >
            <Select
              options={bankOptions}
              placeholder={t("bank-id-placeholder")}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="branchId"
            tooltip={{
              title: t("branch-id-tooltip"),
              icon: <InfoCircleOutlined />,
            }}
            label={t("branch-id-label")}
            rules={[
              { required: true, message: t("messages-field-is-required") },
              { max: 3, message: t("messages-max-3-digits") },
            ]}
          >
            <AutoComplete
              options={branchOptions}
              onSearch={onSearch}
              placeholder={t("branch-id-placeholder")}
              allowClear
              onChange={value => filterAndSetField(form, 'branchId', value)}
            />
          </Form.Item>
          <Form.Item
            name="accountId"
            tooltip={{
              title: t("account-id-tooltip"),
              icon: <InfoCircleOutlined />,
            }}
            label={t("account-id-label")}
            rules={[
              { required: true, message: t("messages-field-is-required") },
              { max: 9, message: t("messages-max-9-digits") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const bankId = getFieldValue("bankId");
                  const branchId = getFieldValue("branchId");
                  const result = validateBankAccount(
                    bankId || "",
                    branchId || "",
                    value || ""
                  );
                  if (!value || result !== RESULT.NOT_VALID) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(t("messages-invalid-bank-details"))
                  );
                },
              }),
            ]}
          >
            <Input 
              placeholder={t("account-id-placeholder")}
              allowClear
              onChange={e => filterAndSetField(form, 'accountId', e.target.value)}
            />
          </Form.Item>
          <Form.Item
            name="payeeId"
            tooltip={{
              title: t("payee-id-tooltip"),
              icon: <InfoCircleOutlined />,
            }}
            label={<span>{t("payee-id-label")} {companyName ? `- ${companyName}` : ""}</span>}
            required
            rules={[
              { required: true, message: t("messages-field-is-required") },
              { max: 9, message: t("messages-max-9-digits") },
              () => ({
                validator(_, value) {
                  const valid = isIsraeliIdValid(value);
                  if (!value || valid) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("messages-invalid-id")));
                },
              }),
            ]}
          >
            <Input 
              placeholder={t("payee-id-placeholder")}
              allowClear
              onChange={async e => {
                const raw = e.target.value || "";
                filterAndSetField(form, 'payeeId', raw);
                await fetchAndSetCompanyNameIfCompany(raw);
              }}
            />
          </Form.Item>
          { !isEdit && (
            <Form.Item>
              <MuiButton
                key={companyName || "empty"}
                disabled={!companyName}
                onClick={() => {
                  // Clean company name: remove special characters, keep only letters and spaces
                  const cleanedName = (companyName || "")
                    .replace(/[^א-תa-zA-Z\s]/g, '') // Keep only Hebrew letters, English letters, and spaces
                    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                    .trim(); // Remove leading/trailing spaces
                  
                  // Limit to 16 characters
                  const limitedName = cleanedName.substring(0, 16);
                  
                  form.setFieldsValue({ payeeName: limitedName });
                }}
                variant="contained"
                color="warning"
                sx={{
                  borderRadius: "8px",
                  height: "40px",
                  padding: "0 20px",
                  fontSize: "14px",
                  fontWeight: "600",
                  textTransform: "none",
                  "&:hover": {
                    transform: companyName ? "translateY(-2px)" : "none",
                  },
                  "&:active": {
                    transform: companyName ? "translateY(0)" : "none",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {t("update-payee-name-button")}
              </MuiButton>
            </Form.Item>
          )}
          <Form.Item
            name="payeeName"
            tooltip={{
              title: t("payee-name-tooltip"),
              icon: <InfoCircleOutlined />,
            }}
            label={t("payee-name-label")}
            rules={[
              { required: true, message: t("messages-field-is-required") },
              { max: 16, message: t("messages-max-16-digits") },
            ]}
          >
            <Input 
              placeholder={t("payee-name-placeholder")}
              allowClear
              onChange={e => filterAndSetField(form, 'payeeName', e.target.value)}
            />
          </Form.Item>
          <Form.Item
            name="payeeNumber"
            tooltip={{
              title: t("employee-number-tooltip"),
              icon: <InfoCircleOutlined />,
            }}
            label={t("employee-number-label")}
            rules={[{ max: 20, message: t("messages-max-20-digits") }]}
          >
            <Input 
              placeholder={t("employee-number-placeholder")}
              allowClear
              onChange={e => filterAndSetField(form, 'payeeNumber', e.target.value)}
            />
          </Form.Item>
          <Form.Item
            name="amount"
            tooltip={{
              title: t("amount-tooltip"),
              icon: <InfoCircleOutlined />,
            }}
            label={t("amount-label")}
            rules={[{ required: true, message: t("messages-field-is-required") }]}
          >
            <InputNumber 
              placeholder={t("amount-placeholder")}
              style={{ width: '100%' }}
              formatter={(value) => {
                if (!value) return '';
                return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              }}
              parser={(value) => {
                if (!value) return '';
                return value.replace(/,/g, '');
              }}
              onChange={value => filterAndSetField(form, 'amount', value)}
            />
          </Form.Item>
          <Form.Item>
            <MuiButton
              disabled={!fieldsValid}
              onClick={onSubmit}
              variant="contained"
              color="warning"
              size="large"
              sx={{ 
                borderRadius: "8px",
                fontWeight: "600",
                height: "40px",
                padding: "0 20px",
                fontSize: "14px",
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: fieldsValid ? "#FF6B35 !important" : "#ccc !important",
                borderColor: fieldsValid ? "#FF6B35" : "#ccc",
                "&:hover": {
                  backgroundColor: fieldsValid ? "#E55A2B !important" : "#ccc !important",
                  borderColor: fieldsValid ? "#E55A2B" : "#ccc",
                }
              }}
            >
              {t(`employee-form-${isEdit ? "edit" : "add"}-button`)}
            </MuiButton>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
});
